/**
 * Route path builders — OPUS-1234.
 *
 * AeDrive now serves two view namespaces off the same app shell:
 *
 *   /competition/:orgId/...   football (drive + copilot) — the original route
 *   /cis/:orgId/...           CIS / rugby
 *
 * Both carry the identical `:orgId/fixture/:fixtureId/renderer/:rendererId`
 * shape, so `TXLiveRoute`'s params work unchanged in either. Which namespace an
 * org belongs to is decided ONLY by the CIS allowlist (config/cisOrgs.js) —
 * never by whatever path the browser happens to be on. That keeps the allowlist
 * authoritative and makes a hand-typed or stale URL self-correcting: the guards
 * in AppRoutes redirect it to the namespace the org actually belongs to.
 *
 * Every redirect in the app builds its path through here. Before this, the
 * `/competition/${orgId}/` template was inlined in 21 places across 6 files,
 * which meant adding a second namespace would have needed all 21 kept in sync
 * by hand.
 */
import { isCisOrg } from '../config/cisOrgs';

/** URL namespace segment per view. */
export const ROUTE_NAMESPACE = {
  FOOTBALL: 'competition',
  CIS: 'cis',
};

/** The namespace segment this org belongs to, per the CIS allowlist. */
export const getNamespaceForOrg = (orgId) =>
  isCisOrg(orgId) ? ROUTE_NAMESPACE.CIS : ROUTE_NAMESPACE.FOOTBALL;

/**
 * The canonical path for an org, optionally deep-linked to a fixture (+
 * renderer, football only).
 *
 *   buildOrgPath('abc')                                  -> /competition/abc/
 *   buildOrgPath('abc', { fixtureId: 'f', rendererId: 'r' })
 *                                                        -> /competition/abc/fixture/f/renderer/r/
 *   buildOrgPath(cisOrg)                                 -> /cis/<cisOrg>/
 *   buildOrgPath(cisOrg, { fixtureId: 'f' })              -> /cis/<cisOrg>/fixture/f/
 *
 * CIS has no renderer to select (read-only view, no operator console), so a
 * rendererId passed in for a CIS org is simply dropped rather than producing
 * a URL no CIS route matches — this is what keeps a football-shaped deep
 * link (fixture+renderer) usable after resolveNamespaceRedirect swaps a
 * newly-allowlisted org from /competition/ onto /cis/: the fixture survives
 * the swap even though the renderer concept doesn't exist on this side.
 *
 * For football, a fixture without a renderer (or vice versa) falls back to
 * the bare org path — that route requires both params, so a half-specified
 * pair would not match any route there.
 */
export const buildOrgPath = (orgId, { fixtureId, rendererId } = {}) => {
  const ns = getNamespaceForOrg(orgId);
  if (ns === ROUTE_NAMESPACE.CIS) {
    return fixtureId ? `/${ns}/${orgId}/fixture/${fixtureId}/` : `/${ns}/${orgId}/`;
  }
  if (fixtureId && rendererId) {
    return `/${ns}/${orgId}/fixture/${fixtureId}/renderer/${rendererId}/`;
  }
  return `/${ns}/${orgId}/`;
};

/**
 * Fixture-only path (no renderer yet) — used by fixture selection, which
 * navigates as soon as a fixture is picked and before a renderer exists.
 */
export const buildFixturePath = (orgId, fixtureId) =>
  `/${getNamespaceForOrg(orgId)}/${orgId}/fixture/${fixtureId}`;

/** True if `pathname` sits under the CIS namespace. */
export const isCisPath = (pathname = window.location.pathname) =>
  String(pathname).startsWith(`/${ROUTE_NAMESPACE.CIS}/`);

/**
 * The namespace-guard decision, as a pure function: given where the browser is
 * and which org owns the session, where (if anywhere) must it be redirected?
 *
 * Returns a path to redirect to, or `null` to stay put. Extracted from the
 * <NamespaceGuard> component so the rule can be tested directly — testing it
 * through the router would mean resolving the entire football page's import
 * graph, which CRA's jest resolver cannot do.
 *
 * Only the /competition/ routes are guarded. The CIS routes are currently open
 * and unguarded, so in practice this decides one thing: whether an allowlisted
 * CIS org that landed on /competition/ gets moved to /cis/.
 */
export const resolveNamespaceRedirect = ({ orgId, pathname, fixtureId, rendererId }) => {
  const expected = getNamespaceForOrg(orgId);
  const onCis = isCisPath(pathname);
  const onExpectedNamespace = expected === ROUTE_NAMESPACE.CIS ? onCis : !onCis;

  if (onExpectedNamespace) return null;

  return buildOrgPath(orgId, { fixtureId, rendererId });
};
